import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStorefront } from './StoreContext.js';

/**
 * Built-in slug palettes for manual visitor switching only
 * (no published store theme and no catalog/editor preview URL).
 */
export const themes = {
  classic: {
    '--hue': '152',
    '--theme-sat': '24%',
  },
  modern: {
    '--hue': '210',
    '--theme-sat': '60%',
  },
  warm: {
    '--hue': '25',
    '--theme-sat': '70%',
  },
  watch: {
    '--hue': '31',
    '--theme-sat': '100%',
  },
};

const DEFAULT_SLUG = 'classic';
const LOCAL_STORAGE_KEY = 'restaurant-theme';

/** Older ThemeContext set full palettes inline; clear so global.css takes over. */
const CLEARED_THEME_VARS = [
  '--first-color',
  '--first-color-alt',
  '--first-color-light',
  '--first-color-lighten',
  '--first-color-dark',
  '--title-color',
  '--text-color',
  '--text-color-light',
  '--body-color',
  '--container-color',
  '--section-surface-1',
  '--section-surface-2',
  '--text-on-primary',
];

const ThemeContext = createContext();

function resolveThemeSlug(candidates) {
  for (const candidate of candidates) {
    const slug = String(candidate || '')
      .trim()
      .toLowerCase();
    if (slug && themes[slug]) return slug;
  }
  return DEFAULT_SLUG;
}

function normalizeSat(value, fallback = '60%') {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*%?$/);
  if (!match) return fallback;
  const n = Math.min(100, Math.max(0, Number(match[1])));
  if (!Number.isFinite(n)) return fallback;
  return `${Math.round(n)}%`;
}

function normalizeHue(value, fallback = '152') {
  const n = Number.parseInt(String(value ?? '').trim(), 10);
  if (!Number.isFinite(n)) return fallback;
  return String(((n % 360) + 360) % 360);
}

const SESSION_STORAGE_PREVIEW_KEY = 'restaurant-theme-preview';

/** Per browsing-context sticky preview (each iframe has its own JS realm). */
let frameStickyPreview = null;

function isTopLevelWindow() {
  if (typeof window === 'undefined') return false;
  try {
    return window.self === window.top;
  } catch {
    return false;
  }
}

function parsePreview(search) {
  const params = new URLSearchParams(search || '');

  // Support explicit exit from preview mode
  if (params.get('clearPreview') === '1') {
    frameStickyPreview = null;
    if (isTopLevelWindow()) {
      try {
        window.sessionStorage.removeItem(SESSION_STORAGE_PREVIEW_KEY);
      } catch {}
    }
    return {
      slug: '',
      hue: '',
      themeSat: '',
      hasKnobs: false,
      forceCatalogPreview: false,
      isPreview: false,
    };
  }

  const themeSlug = String(params.get('themeId') || params.get('theme') || '')
    .trim()
    .toLowerCase();
  const hueParam = params.get('hue');
  const satParam = params.get('themeSat') || params.get('sat');
  const forceCatalogPreview =
    params.get('catalogPreview') === '1' || params.get('preview') === '1';

  const hasHue = hueParam != null && String(hueParam).trim() !== '';
  const hasSat = satParam != null && String(satParam).trim() !== '';

  const isUrlPreview = forceCatalogPreview || Boolean(themeSlug) || hasHue || hasSat;

  if (isUrlPreview) {
    const state = {
      slug: themeSlug,
      hue: hasHue ? normalizeHue(hueParam) : '',
      themeSat: hasSat ? normalizeSat(satParam) : '',
      hasKnobs: hasHue || hasSat,
      forceCatalogPreview,
      isPreview: true,
    };
    frameStickyPreview = state;
    // Persist only for top-level preview navigation — never for dashboard embeds,
    // or live SiteEmbedPreview inherits the last catalog card's hue.
    if (isTopLevelWindow()) {
      try {
        window.sessionStorage.setItem(SESSION_STORAGE_PREVIEW_KEY, JSON.stringify(state));
      } catch {}
    }
    return state;
  }

  // Same iframe/document SPA hop: keep knobs without sharing across sibling embeds.
  if (frameStickyPreview?.isPreview) {
    return frameStickyPreview;
  }

  // Top-level tab only: restore after soft navigations / reloads of a preview link.
  if (isTopLevelWindow()) {
    try {
      const saved = window.sessionStorage.getItem(SESSION_STORAGE_PREVIEW_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && parsed.isPreview) {
          frameStickyPreview = parsed;
          return parsed;
        }
      }
    } catch {}
  }

  return {
    slug: '',
    hue: '',
    themeSat: '',
    hasKnobs: false,
    forceCatalogPreview: false,
    isPreview: false,
  };
}

function knobsForSlug(slug) {
  const base = themes[slug] || themes[DEFAULT_SLUG];
  return {
    hue: base['--hue'],
    themeSat: base['--theme-sat'],
  };
}

function resolveKnobs(defaults, settings, slugFallback) {
  const base = slugFallback || knobsForSlug(DEFAULT_SLUG);
  return {
    hue: normalizeHue(settings?.hue ?? defaults?.hue ?? base.hue, base.hue),
    themeSat: normalizeSat(
      settings?.themeSat ?? defaults?.themeSat ?? base.themeSat,
      base.themeSat,
    ),
  };
}

function readSavedSlug() {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    return saved && themes[saved] ? saved : DEFAULT_SLUG;
  } catch {
    return DEFAULT_SLUG;
  }
}

function writeSavedSlug(slug) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, slug);
  } catch {
    /* private mode / blocked storage */
  }
}

/** Apply --hue / --theme-sat only; CSS derives the rest. */
function applyHueSat(hue, themeSat) {
  const root = document.documentElement;

  CLEARED_THEME_VARS.forEach((property) => {
    root.style.removeProperty(property);
  });

  root.style.setProperty('--hue', normalizeHue(hue, knobsForSlug(DEFAULT_SLUG).hue));
  root.style.setProperty(
    '--theme-sat',
    normalizeSat(themeSat, knobsForSlug(DEFAULT_SLUG).themeSat),
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { store } = useStorefront();
  const { search } = useLocation();

  const preview = useMemo(() => parsePreview(search), [search]);
  const storeTheme = store?.theme || null;
  const storeSlug = storeTheme?.themeSlug || '';
  const storeSettings = storeTheme?.settings || null;
  const storeDefaults = storeTheme?.defaults || null;

  // Preview URL wins over published theme and over localStorage.
  const themeSource = preview.isPreview
    ? 'preview'
    : storeTheme
      ? 'store'
      : 'fallback';

  const canSwitch = themeSource === 'fallback';

  // localStorage only for visitor fallback — never seed from it during preview.
  const [manualSlug, setManualSlug] = useState(() => {
    if (typeof window !== 'undefined' && parsePreview(window.location.search).isPreview) {
      return DEFAULT_SLUG;
    }
    return readSavedSlug();
  });

  const currentTheme = useMemo(() => {
    if (themeSource === 'preview') {
      return preview.slug || DEFAULT_SLUG;
    }
    if (themeSource === 'store') {
      return storeSlug || DEFAULT_SLUG;
    }
    return resolveThemeSlug([manualSlug, DEFAULT_SLUG]);
  }, [themeSource, preview.slug, storeSlug, manualSlug]);

  const slugFallback = useMemo(() => {
    if (themeSource === 'preview' && preview.slug) {
      return knobsForSlug(preview.slug);
    }
    if (themeSource === 'store' && storeSlug) {
      return knobsForSlug(storeSlug);
    }
    return knobsForSlug(themes[currentTheme] ? currentTheme : DEFAULT_SLUG);
  }, [themeSource, preview.slug, storeSlug, currentTheme]);

  const activeKnobs = useMemo(() => {
    // Catalog / Theme Editor preview — URL knobs only (ignore localStorage + live theme).
    if (themeSource === 'preview') {
      return {
        hue: normalizeHue(preview.hue || slugFallback.hue, slugFallback.hue),
        themeSat: normalizeSat(preview.themeSat || slugFallback.themeSat, slugFallback.themeSat),
      };
    }

    if (themeSource === 'store') {
      return resolveKnobs(storeDefaults, storeSettings, slugFallback);
    }

    // Visitor fallback — localStorage / ThemeSwitcher slug only.
    return slugFallback;
  }, [
    themeSource,
    preview.hue,
    preview.themeSat,
    storeDefaults,
    storeSettings,
    slugFallback,
  ]);

  useEffect(() => {
    applyHueSat(activeKnobs.hue, activeKnobs.themeSat);

    // Persist visitor choice only in fallback mode — never during preview or live store theme.
    if (themeSource === 'fallback') {
      writeSavedSlug(currentTheme);
    }
  }, [activeKnobs.hue, activeKnobs.themeSat, themeSource, currentTheme]);

  const switchTheme = (themeName) => {
    if (!themes[themeName]) return;
    if (!canSwitch) return;
    setManualSlug(themeName);
    writeSavedSlug(themeName);
  };

  const value = {
    currentTheme,
    switchTheme,
    availableThemes: Object.keys(themes),
    themeSource,
    canSwitch,
    storeTheme,
    defaults: storeDefaults,
    settings: storeSettings,
    hue: activeKnobs.hue,
    themeSat: activeKnobs.themeSat,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
